// angular imports
import { Component, EventEmitter, Injector, Input, OnInit, Output, ViewChild } from '@angular/core';

// cc-common imports
import { Utils } from 'cc-common';

// devextreme imports
import { DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// component imports
import { WorkOrderBaseComponent } from '../../_base/work-order-base-component';

// constant imports
import { WorkOrderActions } from '../../_constants/workorder-action-constants';
import { VendorConstants } from '../../_constants/vendor-constants';
import { MessageInterval } from '../../../_constants/messages-intervals';

// model imports
import { WorkAuthorizationFileInfoResultDto } from '../../_models/work-authorization-file-info-result-dto';
import { WorkOrderManageWorkAuthorizationResultDto } from '../../_models/work-order-manage-work-authorization-result-dto';
import { WorkOrderBaseParameterDto } from '../../../search/_models/work-order-base-parameter-dto';
import { KeyValueResultDto } from '../../../_models/key-value-result-dto';
import { WorkOrderVendorListResultDto } from '../../_models/work-order-vendor-list-result-dto';
import { WorkAuthorizationVendorParametersDto } from '../../_models/work-authorization-vendor-parameters-dto';
import { WorkAuthorizationVendorEstimateParametersDto } from '../../_models/work-authorization-vendor-estimate-parameters-dto';
import { BaseWorkOrderParameterDto } from '../../_models/base-work-order-parameter-dto';
import { WorkOrderManageProposalsResultDto } from '../../_models/work-order-manage-proposals-result-dto';
import { WorkOrderDocumentAttachRawDto } from '../../_models/work-order-document-attach-raw-dto';
import { WorkAuthorizationAddDocumentParametersDto } from '../../_models/work-authorization-add-document-parameters-dto';
import { WorkOrderWorkAuthorizationFileParameterDto } from '../../_models/work-order-work-authorization-file-parameter-dto';

@Component({
    selector: 'app-manage-work-order-authorization',
    templateUrl: './manage-work-order-authorization.component.html',
    styleUrls: ['./manage-work-order-authorization.component.scss']
})
export class ManageWorkOrderAuthorizationComponent extends WorkOrderBaseComponent implements OnInit {
    @ViewChild('manageWorkAuthorizationPopup', { static: false }) manageWorkAuthorizationPopup: DxPopupComponent;

    @Input() communityId: number;
    @Input() customerId: string;
    @Input() workOrderId: number;

    @Output() onClose = new EventEmitter<void>();
    @Output() onManageProposalShow = new EventEmitter<void>();
    @Output() onWorkOrderEditorShow = new EventEmitter<void>();

    workAuthorizationEditor: WorkOrderManageWorkAuthorizationResultDto =
        new WorkOrderManageWorkAuthorizationResultDto();

    workOrderTitle: string;
    visible = false;

    actionItems: Array<KeyValueResultDto> = [];
    workAuthorizationFiles: Array<WorkAuthorizationFileInfoResultDto> = [];

    showAddVendor = false;
    showUploadManager = false;
    readOnlyVendor = false;
    vendorEstimate: number;

    constructor(protected readonly injector: Injector) {
        super(injector);
        this.onVendorSearchClick = this.onVendorSearchClick.bind(this);
    }

    ngOnInit() {
        this.show();

        this.actionItems = [
            {
                key: WorkOrderActions.GENERATE,
                displayText: 'Generate',
                isDefault: false
            },
            {
                key: WorkOrderActions.GENERATE_AND_EMAIL,
                displayText: 'Generate and Email',
                isDefault: false
            },
            {
                key: WorkOrderActions.UPLOAD,
                displayText: 'Upload',
                isDefault: false
            }
        ];
        this.workOrderTitle = `Manage Work Authorization For Work Order ${this.workOrderId}`;
        this.populateWorkOrderVendorProposals();
        this.populateWorkAuthorizationEditor();
    }

    show(): void {
        this.visible = true;
    }

    onPopupShown(): void {
        this.manageWorkAuthorizationPopup.instance.repaint();
    }

    onPopupClose(): void {
        this.closeManageAuthorization();
    }

    onWorkAuthorizationNotesClick(): void {
        this.onNotesClick();
    }

    onWorkAuthorizationNotesClose(forceReload: boolean): void {
        this.onNotesClose();
        if (Utils.isTrue(forceReload)) {
            this.populateWorkAuthorizationEditor();
        }
    }

    onEmailWorkAuthorization(): void {
        this.emailWorkAuthorization(this.communityId, this.workOrderId);
    }

    getFileIcon(record: WorkAuthorizationFileInfoResultDto): string {
        return this.helperService.getFileIconByExtension(record.fileName);
    }

    onActionItemClick(event: any): void {
        const action = event.itemData;

        switch (action.key) {
            case WorkOrderActions.GENERATE:
                this.generateWorkAuthorization(this.communityId, this.workOrderId);
                break;
            case WorkOrderActions.GENERATE_AND_EMAIL:
                this.generateWorkAuthorization(this.communityId, this.workOrderId, true);
                break;
            case WorkOrderActions.UPLOAD:
                this.showUploadManager = true;
                break;
        }
    }

    onEditWorkOrderClick(): void {
        this.onWorkOrderEditorShow.emit();
        this.closeManageAuthorization();
    }

    onManageProposalsClick(): void {
        this.onManageProposalShow.emit();
        this.closeManageAuthorization();
    }

    onVendorSearchClick(): void {
        this.showAddVendor = true;
    }

    onAddVendorProposalClose(): void {
        this.showAddVendor = false;
    }

    onVendorSelection(records: Array<WorkOrderVendorListResultDto>): void {
        if (Utils.notNullOrEmpty(records)) {
            const selectedVendor: WorkOrderVendorListResultDto = records[0];
            const baseParam: WorkAuthorizationVendorParametersDto = new WorkAuthorizationVendorParametersDto();
            baseParam.communityId = this.communityId;
            baseParam.workOrderId = this.workOrderId;

            if (Utils.notNullAndDefined(selectedVendor.proposalVendorType)) {
                switch (selectedVendor.proposalVendorType) {
                    case VendorConstants.COMMUNITY_VENDOR:
                        baseParam.communityVendorId = selectedVendor.vendorId;
                        break;
                    case VendorConstants.ALL_VENDOR:
                        baseParam.locationVendorId = selectedVendor.vendorId;
                        break;
                    case VendorConstants.TEMP_VENDOR:
                        baseParam.temporaryVendorId = selectedVendor.vendorId;
                        break;
                }
            }

            this.workOrderHelperService.showLoading();
            this.workOrderService.updateWorkAuthorizationVendor(baseParam).subscribe(
                (result: any) => {
                    notify('Vendor updated successfully', 'Success', MessageInterval.successMsgInterval);
                    this.populateWorkAuthorizationEditor();
                    this.workOrderHelperService.hideLoading();
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.workOrderHelperService.hideLoading();
                }
            );
        }
    }

    onVendorEstimateChanged(event: any): void {
        if (
            Utils.notNullAndDefined(event) &&
            Utils.notEquals(this.vendorEstimate, this.workAuthorizationEditor.vendorEstimate)
        ) {
            const baseParam: WorkAuthorizationVendorEstimateParametersDto =
                new WorkAuthorizationVendorEstimateParametersDto();
            baseParam.communityId = this.communityId;
            baseParam.workOrderId = this.workOrderId;
            baseParam.vendorEstimate = this.vendorEstimate;

            this.workOrderHelperService.showLoading();
            this.workOrderService.updateWorkAuthorizationVendorEstimate(baseParam).subscribe(
                (result: any) => {
                    notify('Vendor Estimate updated successfully.', 'Success', MessageInterval.successMsgInterval);
                    this.populateWorkAuthorizationEditor();
                    this.workOrderHelperService.hideLoading();
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.workOrderHelperService.hideLoading();
                }
            );
        }
    }
    onAttachmentUpdate(allFiles: Array<any>): void {
        allFiles.forEach((file) => {
            if (Utils.notNullAndDefined(file['action'])) {
                const document: WorkOrderDocumentAttachRawDto = new WorkOrderDocumentAttachRawDto();
                if (Utils.notNullAndDefined(file.fileId)) {
                    document.fileId = file.fileId;
                    document.communityId = this.communityId;
                    if (Utils.notNullAndDefined(this.customerId)) {
                        document.customerId = this.customerId;
                    }
                }
                document.fileBase64String = Utils.notNullAndDefined(file.fileBase64String)
                    ? file.fileBase64String
                    : null;
                document.fileName = Utils.notNullAndDefined(file.fileName) ? file.fileName : file.name;

                const baseParam: WorkAuthorizationAddDocumentParametersDto =
                    new WorkAuthorizationAddDocumentParametersDto();
                baseParam.communityId = this.communityId;
                baseParam.workOrderId = this.workOrderId;
                baseParam.document = document;

                this.workOrderHelperService.showLoading();
                this.workOrderService.uploadWorkAuthorizationDocument(baseParam).subscribe(
                    (result: any) => {
                        this.workOrderHelperService.hideLoading();
                        setTimeout(() => {
                            this.populateWorkAuthorizationEditor();
                        }, 200);
                    },
                    (error) => {
                        this.errorService.displayError(error);
                        this.workOrderHelperService.hideLoading();
                    }
                );
            }
        });
    }

    onDownloadFile(record: WorkAuthorizationFileInfoResultDto): void {
        const baseParams: WorkOrderWorkAuthorizationFileParameterDto = new WorkOrderWorkAuthorizationFileParameterDto();
        baseParams.fileId = record.fileId;
        baseParams.communityId = this.communityId;
        baseParams.workOrderId = this.workOrderId;
        this.workOrderHelperService.showLoading();
        this.workOrderService.downloadWorkAuthorizationDocument(baseParams).subscribe(
            (fileData: Blob) => {
                this.helperService.downloadFileUsingBlob(fileData, record.fileName);
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    onAttachmentUploaderClose(): void {
        this.showUploadManager = false;
        this.populateWorkAuthorizationEditor();
    }

    private generateWorkAuthorization(communityId: number, workOrderId: number, emailAuthorization = false): void {
        const baseParams: BaseWorkOrderParameterDto = new BaseWorkOrderParameterDto();
        baseParams.communityId = communityId;
        baseParams.workOrderId = workOrderId;

        this.workOrderHelperService.showLoading();

        this.workOrderService.generateWorkAuthorization(baseParams).subscribe(
            (result: any) => {
                if (Utils.isTrue(emailAuthorization)) {
                    this.emailWorkAuthorization(communityId, workOrderId);
                } else {
                    notify('Work Authorization generated successfully.', 'Success', MessageInterval.successMsgInterval);
                }
                this.populateWorkAuthorizationEditor();
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private populateWorkOrderVendorProposals(): void {
        const baseParam: BaseWorkOrderParameterDto = new BaseWorkOrderParameterDto();
        baseParam.communityId = this.communityId;
        baseParam.workOrderId = this.workOrderId;

        this.workOrderHelperService.showLoading();
        this.workOrderService.manageProposals(baseParam).subscribe(
            (result: WorkOrderManageProposalsResultDto) => {
                // setting flag to set edit mode of Vendor and Vendor estimate
                this.readOnlyVendor = Utils.notNullOrEmpty(result.workOrderProposals);
                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private populateWorkAuthorizationEditor(): void {
        const baseParams: WorkOrderBaseParameterDto = new WorkOrderBaseParameterDto();
        baseParams.communityId = this.communityId;
        baseParams.workOrderId = this.workOrderId;

        this.workOrderHelperService.showLoading();
        this.workOrderService.getWorkAuthorization(baseParams).subscribe(
            (result: WorkOrderManageWorkAuthorizationResultDto) => {
                this.workAuthorizationEditor = result;
                this.workAuthorizationFiles = result.files;
                this.vendorEstimate = result.vendorEstimate;

                this.workOrderHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.workOrderHelperService.hideLoading();
            }
        );
    }

    private closeManageAuthorization(): void {
        this.visible = false;
        this.onClose.emit();
    }
}
